/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import StyledEditProfile from "./StyledEditProfile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { INPUT_LABELS, PROFILE_PAGE } from "../../constants/lang";
import TextFeild from "../../components/CommonComponents/TextFeild";
import { GENDER_OPTIONS, SIZE_OPTIONS } from "../../constants/optionsConfig";
import SelectFeild from "../../components/CommonComponents/Select";
import { COUNTRIES } from "../../constants/countryCode";
import downarrow from "../../assets/icons/down-arrow1.svg";
import { Button } from "rsuite";
import { editProfileSchema } from "../../schema/signupSchema";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProfile,
  updateProfile,
  fetchSizes,
} from "../../store/reducers/home/apiThunk";
import {
  ToastNotifyError,
  ToastNotifySuccess,
} from "../../components/CommonComponents/Toast/ToastNotify";
import { updateLoading } from "../../store/reducers/loader";
import { ROUTE_PATH } from "../../constants/route";
import { useNavigate } from "react-router-dom";
import profileicon from "../../assets/icons/profile-new.svg";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CameraAltOutlined from "@mui/icons-material/CameraAltOutlined";
import ImageCropper from "../../components/CommonComponents/ImageCropper/index.jsx";
import { ACCEPT_OPTIONS } from "../../constants/optionsConfig";
import axios from "axios";

const EditProfile = () => {
  const authentication = useSelector((state) => state.authentication);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sizes, setSizes] = useState([]);
  const popupRef = useRef(null);
  const avatarRef = useRef(null);
  const fileInputRef = useRef(null);
  const [profileImage, setProfileImage] = useState(profileicon);
  const [openCropperModal, setOpenCropperModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [croppedImage, setCroppedImage] = useState(profileicon);
  const [croppedImageFile, setCroppedImageFile] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getProfile = async () => {
    try {
      const response = await dispatch(fetchProfile());
      const { data, message, status } = response?.payload;
      if (status) {
        setProfileData(data[0]);
      } else {
        ToastNotifyError(message);
      }
    } catch {
      ToastNotifyError("Failed to fetch profile");
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setFocus,
  } = useForm({
    resolver: yupResolver(editProfileSchema),
  });



  const onSubmit = async (data) => {
    updateLoading(true);
    const capitalizeToogle =
      String(data?.toggle_hide_size || "")
        .charAt(0)
        .toUpperCase() +
      String(data?.toggle_hide_size || "")
        .slice(1)
        .toLowerCase();
    const dataToSend = {
      username: data?.name,
      clothing_preference: data?.clothingPreference,
      sizes: data?.sizes,
      age: data?.age,
      country_code: data?.country_code?.dial_code,
      toggle_hide_size: capitalizeToogle,
      mobile_number: data?.mobile_number,
      profile_picture: profileImage,
      location: data?.location
    };
    try {
      const res = await dispatch(updateProfile(dataToSend));
      if (res?.payload?.status) {
        ToastNotifySuccess("Profile updated successfully");
        getProfile();
        updateLoading(false);
      } else {
        ToastNotifyError(res?.payload?.message);
        updateLoading(false);
      }
    } catch {
      ToastNotifyError("Failed to update profile");
    } finally {
      updateLoading(false);
    }
  };

  useEffect(() => {
    setValue(
      "country_code",
      COUNTRIES.find((country) => country.dial_code === "+1")
    );
  }, [setValue]);

  const getSizes = async () => {
    try {
      const response = await dispatch(fetchSizes());
      const { data, status } = response?.payload;
      if (status && Array.isArray(data)) {
        setSizes(data);
      } else {
        ToastNotifyError("Failed to fetch sizes");
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
      ToastNotifyError("Failed to fetch sizes");
    }
  };

  useEffect(() => {
    getProfile();
    getSizes();
  }, []);

  useEffect(() => {
    setValue("name", profileData?.username);
    setValue("age", profileData?.age);
    setValue("sizes", profileData?.sizes);
    setValue("mobile_number", profileData?.mobile_number);
    setValue(
      "clothingPreference",
      GENDER_OPTIONS.find((opt) => opt.key === profileData?.clothing_preference)
        ?.key || ""
    );
    setValue("toggle_hide_size", String(profileData?.toggle_hide_size).toLowerCase() === "true" ? "true" : "false");
    if (profileData?.profile_picture) {
      setCroppedImage(profileData.profile_picture);
      setProfileImage(profileData.profile_picture);
    } else {
      setCroppedImage(profileicon);
      setProfileImage(profileicon);
    }
    setValue("location", profileData?.location);
  }, [profileData]);

  const togglePopup = (event) => {
    event.stopPropagation();
    if (popupRef.current) {
      popupRef.current.style.display =
        popupRef.current.style.display === "block" ? "none" : "block";
    }
  };

  const removeProfile = () => {
    setCroppedImage(profileicon);
    setProfileImage(profileicon);
    setImageUrl(null);
    popupRef.current.style.display = "none";
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ACCEPT_OPTIONS.includes(file.type)) {
      ToastNotifyError(
        "Invalid file type! Please select a PNG, JPEG, JPG, or WEBP image."
      );
      e.target.value = "";
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      ToastNotifyError("File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageUrl(reader.result);
      setOpenCropperModal(true);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleClickOutside = (event) => {
    if (
      popupRef.current &&
      !popupRef.current.contains(event.target) &&
      avatarRef.current !== event.target
    ) {
      popupRef.current.style.display = "none";
    }
  };

  const handleClick = (event) => {
    handleClickOutside(event);
  };

  const handleCancelCropper = () => {
    setOpenCropperModal(false);
    setImageUrl(null);
    popupRef.current.style.display = "none";
  };

  const handleImageCrop = ({ base64, file }) => {
    setLoading(true); // Set loading to true before changing the image
    setCroppedImage(base64);
    setCroppedImageFile(file);
    setImageUrl(null);
    setOpenCropperModal(false);
  };

  useEffect(() => {
    if (croppedImageFile !== null) {
      handleImageUpload();
    }
  }, [croppedImageFile]);

  const handleImageUpload = async (e) => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}v1/api/accounts/upload-image/`,
        "",
        {
          headers: {
            // "Content-Type": file.type,
            Authorization: authentication.auth.data.data.auth_token,
          },
        }
      );

      if (response.data.status) {
        const { upload_url, key } = response.data.data;
        const uploadResponse = await axios.put(upload_url, croppedImageFile, {
          headers: {
            "Content-Type": croppedImageFile.type,
          },
        });

        if (uploadResponse.status === 200) {
          setProfileImage(process.env.REACT_APP_S3_IMAGES_URL + key);
          ToastNotifySuccess("Image uploaded successfully!");
          setLoading(false);
        } else {
          ToastNotifyError("Failed to upload the image to S3.");
        }
      } else {
        ToastNotifyError("Failed to get the upload URL.");
      }
    } catch (error) {
      ToastNotifyError("There was an error uploading the image.");
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(errors)?.length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        setFocus(firstErrorField);
        // Increase timeout to give DOM more time to update
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          console.log("Error field:", firstErrorField, "Element found:", errorElement);
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 500); // Increased from 300ms to 500ms
      }
    }
  }, [errors, setFocus]);

  return (
    <StyledEditProfile>
      <Box className='edit-profile-container'>
        <Box className='back-btn-edit-profile'>
          <ArrowBackIcon
            sx={{ fontSize: "20px", cursor: "pointer" }}
            onClick={() => navigate(ROUTE_PATH.USER_PROFILE)}
          />
          <Typography className='order-history-title'>Edit Profile</Typography>
        </Box>
        <Box
          className='edit-profile-form-section'
          component='form'
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box
            position='relative'
            className='avatar-container'
            onClick={(e) => handleClick(e)}
          >
            {loading && (
              <Box className='loader-container'>
                <CircularProgress size={40} />
              </Box>
            )}
            <>
              <Avatar
                ref={avatarRef}
                src={croppedImage}
                alt='Profile Picture'
                className='Profile-Picture'
                imgProps={{
                  onLoad: () => setLoading(false),
                  onError: () => setLoading(false)
                }}
              />
              <Box onClick={(e) => togglePopup(e)} className='camera-icon'>
                <CameraAltOutlined />
              </Box>
            </>

            <input
              type='file'
              ref={fileInputRef}
              accept={ACCEPT_OPTIONS}
              style={{ display: "none" }}
              onChange={handleImageChange}
            />

            {/* Popup Menu */}
            <Box ref={popupRef} className='popup-menu'>
              {/* Change Profile */}
              <Box
                onClick={() => fileInputRef.current.click()}
                className='menu-item'
                aria-label='Change profile picture'
              >
                <ImageOutlinedIcon className='menu-icon' />
                <Typography className='change-profile'>
                  {PROFILE_PAGE.changeProfile}
                </Typography>
              </Box>

              {/* Remove Profile */}
              <Box onClick={() => removeProfile()} className='menu-item remove'>
                <DeleteOutlineOutlinedIcon className='menu-icon' />
                <Typography className='remove-profile'>
                  {PROFILE_PAGE.RemoveProfile}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box className='name'>
            <Controller
              name='name'
              control={control}
              className='name-feild'
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  id="name"
                  label={INPUT_LABELS.name}
                  placeholder='Enter your name'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  required
                  tooltip
                  tooltipText='Used for reviews'
                  {...field}
                />
              )}
            />
          </Box>
          <Box className='age'>
            <Controller
              name='age'
              control={control}
              className='age-feild'
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  id="age"
                  type='number'
                  label={INPUT_LABELS.age}
                  placeholder='Enter your age'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.age}
                  helperText={errors.age?.message}
                  {...field}
                />
              )}
            />
          </Box>
          <Box className='clothing-preference' sx={{ marginTop: "10px" }}>
            <Typography className='subtitle3'>
              {PROFILE_PAGE.clothingPreference}
            </Typography>
            <Controller
              name='clothingPreference'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <div className='custom-radio-group'>
                  {GENDER_OPTIONS.map((option) => (
                    <div
                      key={option.key}
                      className={`custom-radio ${field.value === option.key ? "selected" : ""}`}
                      onClick={() => field.onChange(option.key)}
                    >
                      {option.value}
                    </div>
                  ))}
                </div>
              )}
            />
          </Box>
          <Box className='size'>
            <Controller
              name='sizes'
              control={control}
              defaultValue={""}
              render={({ field }) => (
                <SelectFeild
                  {...field}
                  id='sizes'
                  label='Sizes'
                  placeholder={PROFILE_PAGE.enterYourSize}
                  options={sizes.length > 0
                    ? sizes?.map((size) => size?.label)
                    : SIZE_OPTIONS?.map((size) => size?.value)
                  }
                  value={
                    sizes.length > 0
                      ? sizes?.find((size) => size?.id === field.value)?.label || ""
                      : SIZE_OPTIONS?.find((size) => size?.key === field.value)?.value || ""
                  }
                  onChange={(event) => {
                    const selectedValue = event.target.value;
                    if (sizes.length > 0) {
                      // Use API data with size ID
                      const selectedSize = sizes?.find((size) => size?.label === selectedValue);
                      field.onChange(selectedSize?.id || "");
                    } else {
                      // Fallback to static options
                      const selectedKey = SIZE_OPTIONS.find(
                        (size) => size.value === selectedValue
                      )?.key;
                      field.onChange(selectedKey || "");
                    }
                  }}
                  error={!!errors.sizes}
                  helperText={errors.sizes?.message}
                />
              )}
            />
          </Box>
          <Box className='switch-container'>
            <Box className='custom-switch-label'>
              <Controller
                name='toggle_hide_size'
                control={control}
                value='false'
                render={({ field }) => (
                  <>
                    <Switch
                      {...field}
                      className={`custom-switch ${field.value === "true" ? "active" : ""}`}
                      onChange={(event) =>
                        field.onChange(event.target.checked.toString())
                      }
                    />
                    <span className='custom-switch-text'>
                      {PROFILE_PAGE.hideSize}
                    </span>
                  </>
                )}
              />
            </Box>
          </Box>
          <Box className='input-group'>
            <Typography className='subtitle3'>
              {PROFILE_PAGE.mobileNumber}
            </Typography>
            <Box className='mobile-input-group'>
              <Controller
                name='country_code'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={COUNTRIES}
                    getOptionLabel={(option) => option?.dial_code || ""}
                    className='custom-country-dropdown'
                    popupIcon={
                      <img
                        src={downarrow}
                        alt='Dropdown Icon'
                        className='custom-country-dropdown-icon'
                      />
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: field.value?.flag ? (
                            <InputAdornment position='start'>
                              <img
                                src={field.value.flag}
                                alt={field.value.name}
                                style={{ width: 24, height: 18 }}
                              />
                            </InputAdornment>
                          ) : null,
                        }}
                        className='country-code-input'
                        sx={{
                          ".MuiInputBase-root": {
                            width: "120px",
                            height: "50px",
                          },
                          ".MuiSvgIcon-root": {
                            display: "none",
                          },
                        }}
                      />
                    )}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    renderOption={(props, option) => (
                      <MenuItem {...props}>
                        <img
                          src={option.flag}
                          alt={option.name}
                          style={{ width: 20, height: 15, marginRight: 10 }}
                        />
                        {option.dial_code}
                      </MenuItem>
                    )}
                    value={field.value ?? null}
                  />
                )}
              />

              <Controller
                name='mobile_number'
                control={control}
                defaultValue=''
                render={({ field }) => (
                  <TextField
                    id="mobile_number"
                    {...field}
                    type='tel'
                    max
                    placeholder={PROFILE_PAGE.enterYourNumber}
                    className='mobile-number-input'
                    error={!!errors.mobile_number}
                    helperText={errors.mobile_number?.message}
                    inputProps={{
                      pattern: "[0-9]*",
                      maxLength: 13,
                    }}
                    sx={{
                      height: "50px",
                      "& .MuiInputBase-root": {
                        height: "50px",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: "14px",
                      },
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        isNaN(Number(e.key))
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
            </Box>
          </Box>
          <Box className='input-group-2'>
            <Typography
              variant='subtitle3'
              sx={{ fontFamily: "Lexend", fontSize: "12px", fontWeight: "400", marginBottom: "-10px" }}
            >
              {PROFILE_PAGE.locationLabel}
            </Typography>
            <Controller
              name='location'
              control={control}
              defaultValue={""}
              render={({ field }) => (
                <TextFeild
                  id="location"
                  {...field}
                  placeholder={PROFILE_PAGE.enterYourLocation}
                  fullWidth
                  margin='normal'
                  error={!!errors.location}
                  helperText={errors.location?.message}
                  sx={{ marginTop: "5px" }}
                />
              )}
            />
          </Box>
          <Box className='submit-btn'>
            <Button
              variant='contained'
              color='primary'
              fullWidth
              onClick={handleSubmit(onSubmit)}
              aria-label='Submit profile changes'
              className='custom-save-button'
            >
              Save changes
            </Button>
          </Box>
        </Box>
      </Box>
      <Modal
        open={openCropperModal}
        onClose={() => setOpenCropperModal(false)}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "40%",
            height: "60%",
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 3,
            padding: 2,
          }}
        >
          <Box
            className='image-crop-display'
            sx={{ height: "90%", width: "100%" }}
          >
            {imageUrl && (
              <ImageCropper
                src={imageUrl}
                onCancel={handleCancelCropper}
                onCropComplete={handleImageCrop}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </StyledEditProfile>
  );
};

export default EditProfile;
